import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// 从本地存储获取上传的视频
const getUploadedVideos = () => {
  try {
    // 检查是否在浏览器环境中
    if (typeof window !== 'undefined' && window.localStorage) {
      const storedVideos = localStorage.getItem('uploadedVideos');
      console.log('Stored videos:', storedVideos);
      const videos = JSON.parse(storedVideos || '[]');
      // 为没有hidden属性的视频添加默认值
      return videos.map(video => ({
        ...video,
        hidden: video.hidden !== undefined ? video.hidden : false
      }));
    }
    return [];
  } catch (error) {
    console.error('Error reading uploaded videos:', error);
    return [];
  }
};

// 从后端数据库获取视频列表
const getVideosFromDB = async () => {
  try {
    const response = await api.get('/videos');
    if (response.data.success) {
      console.log('从数据库获取的视频:', response.data.videos);
      return response.data.videos;
    }
    return [];
  } catch (error) {
    console.error('从数据库获取视频失败:', error);
    return [];
  }
};

// 从后端数据库获取视频列表并转换为本地存储格式
const getVideosFromDBAsLocalFormat = async () => {
  try {
    const videosFromDB = await getVideosFromDB();
    console.log('从数据库获取的原始视频:', videosFromDB);
    
    // 转换为与本地存储相同的格式
    const formattedVideos = videosFromDB.map(video => ({
      id: video.videoId,
      title: video.title,
      description: '',
      category: video.category,
      subcategory: video.subcategory || '',
      level: 'Starters',
      duration: video.duration || 0,
      fileUrl: video.videoUrl.startsWith('http') ? video.videoUrl : `${window.location.origin}${video.videoUrl}`,
      subtitleUrl: video.subtitleFiles && video.subtitleFiles.length > 0 
        ? (video.subtitleFiles[0].startsWith('http') 
            ? video.subtitleFiles[0] 
            : `${window.location.origin}${video.subtitleFiles[0]}`)
        : '',
      posterUrl: video.posterUrl 
        ? (video.posterUrl.startsWith('http') 
            ? video.posterUrl 
            : `${window.location.origin}${video.posterUrl}`)
        : '',
      fileType: video.fileType || 'video',
      hidden: false,
      uploadedAt: video.uploadTime,
      subtitleFiles: video.subtitleFiles || []
    }));
    
    console.log('转换后的视频:', formattedVideos);
    
    // 更新localStorage以保持同步
    localStorage.setItem('uploadedVideos', JSON.stringify(formattedVideos));
    
    return formattedVideos;
  } catch (error) {
    console.error('从数据库获取并转换视频失败:', error);
    // 如果数据库获取失败，回退到localStorage
    return getUploadedVideos();
  }
};

// 只使用上传的视频，不使用模拟数据 - 优先从数据库获取
const getUploadedCourses = async () => {
  let uploadedVideos;
  
  try {
    uploadedVideos = await getVideosFromDBAsLocalFormat();
  } catch (error) {
    console.error('获取数据库视频失败，使用本地存储:', error);
    uploadedVideos = getUploadedVideos();
  }
  
  console.log('getUploadedCourses - 使用的视频:', uploadedVideos);
  return uploadedVideos
    .filter(video => !video.hidden) // 只包含非隐藏的视频
    .map(video => ({
      _id: `uploaded_${video.id}`,
      title: video.title,
      description: video.description,
      category: video.category,
      subcategory: video.subcategory || '',
      level: video.level,
      duration: video.duration,
      thumbnail: video.posterUrl,
      fileType: video.fileType || 'video',
      tags: [video.category === 'human' ? '人体科学' : 
             video.category === 'universe' ? '宇宙科学' : 
             video.category === 'physics' ? '物理学' : 
             video.category === 'chemistry' ? '化学' : 
             video.category === 'earth' ? '地球科学' : 
             video.category === 'biology' ? '生物学' : 
             video.category === 'plants' ? '植物学' : 
             video.category === 'animals' ? '动物学' : 
             video.category === 'microbes' ? '微生物学' : '生态学'],
      lessons: [
        {
          _id: `uploaded_lesson_${video.id}`,
          title: video.title,
          type: video.fileType === 'video' ? '视频' : (video.fileType === 'audio' ? '音频' : '文档'),
          duration: video.duration,
          fileType: video.fileType,
          content: {
            videoUrl: video.fileUrl,
            subtitleUrl: video.subtitleUrl,
            posterUrl: video.posterUrl
          }
        }
      ]
    }));
};

export const authAPI = {
  register: async (data) => {
    const response = await api.post('/auth/register', data);
    return response;
  },
  login: async (data) => {
    const response = await api.post('/auth/login', data);
    return response;
  },
  logout: async () => {
    const response = await api.post('/auth/logout');
    return response;
  },
  getMe: async () => {
    const response = await api.get('/auth/me');
    return response;
  }
};

export const userAPI = {
  getProfile: async () => {
    // 直接返回模拟数据，不尝试调用API
    return {
      data: {
        user: {
          id: '1',
          phone: '13800138000',
          username: 'user_8000',
          fullName: '',
          role: 'user',
          level: 'beginner'
        }
      }
    };
  },
  updateProfile: async (data) => {
    // 直接返回模拟数据，不尝试调用API
    return {
      data: {
        user: {
          id: '1',
          phone: '13800138000',
          username: 'user_8000',
          fullName: data.fullName || '',
          role: 'user',
          level: 'beginner'
        }
      }
    };
  },
  updatePassword: async (data) => {
    // 直接返回模拟数据，不尝试调用API
    return {
      data: {
        success: true
      }
    };
  },
  getLeaderboard: async () => {
    // 直接返回模拟数据，不尝试调用API
    return {
      data: {
        users: []
      }
    };
  }
};

export const courseAPI = {
  getCourses: async (params) => {
    console.log('========== courseAPI.getCourses 开始 ==========');
    console.log('params:', params);
    
    // 从数据库获取视频数据
    const uploadedCourses = await getUploadedCourses();
    let filteredCourses = [...uploadedCourses];
    
    if (params?.category) {
      filteredCourses = filteredCourses.filter(course => course.category === params.category);
    }
    
    if (params?.subcategory) {
      filteredCourses = filteredCourses.filter(course => course.subcategory === params.subcategory);
    }
    
    return {
      data: {
        courses: filteredCourses,
        currentPage: 1,
        totalPages: 1,
        total: filteredCourses.length
      }
    };
  },
  getFeatured: async () => {
    // 只返回上传的视频
    const uploadedCourses = await getUploadedCourses();
    const featuredCourses = uploadedCourses.slice(0, 4);
    return {
      data: {
        courses: featuredCourses
      }
    };
  },
  getCourse: async (id) => {
    console.log('=== getCourse 被调用，参数 id:', id);
    // 只从上传的视频中查找
    const uploadedCourses = await getUploadedCourses();
    console.log('上传的课程列表:', uploadedCourses);
    
    let course = uploadedCourses.find(c => c._id === id);
    console.log('通过精确ID查找的结果:', course);
    
    // 如果找不到匹配的课程，尝试通过category查找
    if (!course && id.includes('category_')) {
      const category = id.replace('category_', '');
      course = uploadedCourses.find(c => c.category === category);
      console.log('通过category查找的结果:', course);
    }
    
    // 如果还是找不到，尝试匹配不带前缀的ID
    if (!course) {
      course = uploadedCourses.find(c => {
        const courseId = c._id.replace('uploaded_', '');
        return courseId === id || c._id === id;
      });
      console.log('通过不带前缀ID查找的结果:', course);
    }
    
    console.log('最终返回的课程:', course || uploadedCourses[0]);
    
    return {
      data: {
        course: course || uploadedCourses[0]
      }
    };
  },
  enrollCourse: async (id) => {
    // 直接返回模拟数据，不尝试调用API
    return {
      data: {
        success: true
      }
    };
  }
};

export const progressAPI = {
  getProgress: async () => {
    // 直接返回模拟数据，不尝试调用API
    return {
      data: {
        progress: []
      }
    };
  },
  getCourseProgress: async (courseId) => {
    // 直接返回模拟数据，不尝试调用API
    return {
      data: {
        progress: {
          course: courseId,
          lessons: []
        }
      }
    };
  },
  completeLesson: async (lessonId, data) => {
    // 直接返回模拟数据，不尝试调用API
    return {
      data: {
        success: true
      }
    };
  },
  getStats: async () => {
    // 直接返回模拟数据，不尝试调用API
    return {
      data: {
        stats: {
          totalMinutes: 0,
          completedLessons: 0,
          streak: 0
        }
      }
    };
  }
};

export const videoAPI = {
  getVideos: async () => {
    const response = await api.get('/videos');
    return response;
  },
  getVideo: async (videoId) => {
    const response = await api.get(`/videos/${videoId}`);
    return response;
  }
};

export const vocabularyAPI = {
  smartExtract: async (data) => {
    const response = await api.post('/vocabulary/smart-extract', data);
    return response;
  },
  getVideoVocabulary: async (videoId) => {
    const response = await api.get(`/vocabulary/video/${videoId}`);
    return response;
  },
  saveVideoVocabulary: async (videoId, data) => {
    const response = await api.post(`/vocabulary/video/${videoId}`, data);
    return response;
  },
  getHistory: async () => {
    const response = await api.get('/vocabulary/history');
    return response;
  },
  resetHistory: async () => {
    const response = await api.post('/vocabulary/history/reset');
    return response;
  }
};

export default api;
